import { useCallback } from "react";

import { useHistory } from "react-router-dom";
import { AnalyticsDataSource, Roles, type RunReportChannelStatus } from "@doitintl/cmp-models";

import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { useIsFeatureEntitled } from "../../../Context/TierProvider";
import { consoleErrorWithSentry } from "../../../utils";
import mixpanel from "../../../utils/mixpanel";
import { useQuery } from "../../../utils/useQuery";
import { useCloudAnalyticsContext } from "../CloudAnalyticsContext";
import useGenerateReport from "../generateReport";
import { type PayloadConfig } from "../generateReport/types";
import { getOrganizationRef } from "../utilities";
import { createRunReportChannel } from "./db";

export const useNewReportHandler = ({
  baseUrl,
  mixpanelEventName,
  additionalState = {},
  replaceCurrentRoute = false,
}: {
  baseUrl: string;
  mixpanelEventName: string;
  additionalState?: object;
  replaceCurrentRoute?: boolean;
}) => {
  const { customer, userOrganization, init: customerCtxInit } = useCustomerContext();
  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const { isDoitEmployee } = useAuthContext({ mustHaveUser: true });
  const { defaultReportConfig } = useCloudAnalyticsContext();
  const history = useHistory();
  const generateReport = useGenerateReport();
  const query = useQuery();

  return useCallback(async () => {
    if (customerCtxInit && currentUser.email) {
      const orgRef = getOrganizationRef(isDoitEmployee, userOrganization, customer.id);
      try {
        const newReportId = await generateReport(
          {
            description: "",
            customer: customer.ref,
            organization: orgRef,
            name: "",
            collaborators: [{ email: currentUser.email, role: Roles.OWNER }],
            config: defaultReportConfig as PayloadConfig,
            draft: true,
          },
          true
        );
        mixpanel.track(mixpanelEventName, { reportId: newReportId, draft: true });
        const reportUrl = `${baseUrl}/${newReportId}`;
        const reportUrlWithQuery = query.size > 0 ? `${reportUrl}?${query.toString()}` : reportUrl;

        if (replaceCurrentRoute) {
          history.replace(reportUrlWithQuery, {
            ...additionalState,
          });
        } else {
          history.push(reportUrlWithQuery, {
            ...additionalState,
          });
        }
      } catch (error) {
        consoleErrorWithSentry(error);
      }
    }
  }, [
    customerCtxInit,
    currentUser.email,
    isDoitEmployee,
    userOrganization,
    customer.id,
    customer.ref,
    generateReport,
    defaultReportConfig,
    mixpanelEventName,
    baseUrl,
    query,
    replaceCurrentRoute,
    history,
    additionalState,
  ]);
};

export const useCreateRunReportChannel = ({ status }: { status: RunReportChannelStatus }) => {
  const { currentUser } = useAuthContext({ mustHaveUser: true });

  return useCallback(
    async () =>
      createRunReportChannel({
        uid: currentUser.uid,
        email: currentUser.email,
        status,
      }),
    [currentUser.email, currentUser.uid, status]
  );
};

export const useIsCustomerEntitledToDatasource = (datasource: AnalyticsDataSource) => {
  const isEntitledToK8s = useIsFeatureEntitled("lens:k8s");

  if (datasource === AnalyticsDataSource.KUBERNETES_UTILIZATION) {
    return isEntitledToK8s ?? false;
  }

  return true;
};
